/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { forwardRef, memo, useCallback } from 'react';
import { Platform, type TextInput } from 'react-native';
import { TextField } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useA11ySelector } from '@order/utils';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationSearchField = memo(
  forwardRef<TextInput, LocationSearchFieldProps>((props, ref) => {
    const {
      value,
      onSubmit,
      onChangeText,
      onClear,
      isDisabled = false,
      placeholder,
      enterKeyHint,
      dataDetectorTypes,
      style,
    } = props;

    const a11ySelector = useA11ySelector('locations.search-field');
    const { t } = useLocalizationContext();

    /**
     * A fallback for when the `onSubmit` prop is not provided to re-focus
     * the input on the web platform.
     */
    const onSubmitFallback = useCallback(() => {
      if (typeof ref === 'function' || Platform.OS !== 'web') return;

      ref?.current?.focus();
    }, [ref]);

    return (
      <TextField
        ref={ref}
        hideNoticeSpace
        containerTestID="locationsearch.location-search-bar"
        submitButtonTestID="locationsearch.submit-button"
        testID="location-search-input"
        accessibilityLabel={t('location.search-placeholder')}
        placeholder={placeholder}
        style={style}
        clearButtonType="text"
        value={value}
        dataDetectorTypes={dataDetectorTypes}
        enterKeyHint={enterKeyHint}
        onChangeText={onChangeText}
        onClear={onClear}
        onSubmit={onSubmit ?? onSubmitFallback}
        hideSubmitButton={!onSubmit}
        disabled={isDisabled}
        clearBtnLabel={t('general.clear')}
        clearBtnAccessibilityHint={t('general.clear-a11y-hint')}
        submitBtnAccessibilityLabel={t('general.submit')}
        submitAccessibilityHint={t('general.submit-a11y-hint')}
        {...a11ySelector.props}
      />
    );
  }),
);

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchFieldProps = Readonly<{
  value: string;
  isDisabled?: boolean;
  onClear?: () => void;
  placeholder: string;
}> &
  Pick<
    ComponentProps<typeof TextField>,
    'onSubmit' | 'onChangeText' | 'style' | 'enterKeyHint' | 'dataDetectorTypes'
  >;
